import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Building2, Check, Loader2 } from "lucide-react";
import { useBusinessAuth } from "@/lib/businessAuth";
import { apiError } from "@/lib/businessApi";
import MilesLogo from "@/components/Logo";
import { useLang } from "@/lib/i18n";
import { useRobots } from "@/components/seo/Seo";

const inputCls = "h-14 w-full rounded-2xl border border-ink/10 bg-white px-4 text-[15px] font-medium text-ink outline-none transition-[border-color,box-shadow] placeholder:font-normal placeholder:text-slate/55 focus:border-miles focus:ring-4 focus:ring-miles/10";

export default function BusinessAuth({ mode = "login" }) {
  const isLogin = mode === "login";
  const { t } = useLang();
  useRobots("noindex, nofollow");
  const { login, signup } = useBusinessAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ org_name: "", name: "", email: "", password: "", business_type: "company" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await signup(form);
      navigate("/business/app");
    } catch (err) {
      setError(apiError(err, t("bz.auth.err_generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-2" data-testid="business-auth-page">
      <div className="grain relative hidden overflow-hidden bg-miles-deep lg:flex lg:flex-col lg:justify-between lg:p-14">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <Link to="/" className="relative inline-flex items-center" data-testid="auth-logo" aria-label="MILES — retour à l'accueil">
          <MilesLogo variant="white" className="h-7" testId="auth-logo-img" />
        </Link>
        <div className="relative">
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("bz.auth.panel_eyebrow")}</p>
          <h1 className="mt-5 max-w-md text-4xl font-semibold leading-[1.05] tracking-[-0.04em] text-white xl:text-5xl">
            {t("bz.auth.panel_h1a")}
            <br />
            <span className="font-light text-white/60">{t("bz.auth.panel_h1b")}</span>
          </h1>
          <div className="mt-10 space-y-4">
            {t("bz.auth.panel_points").map((point) => (
              <p key={point} className="flex items-center gap-3 text-sm text-white/80">
                <Check className="h-4 w-4 shrink-0 text-miles-electric" strokeWidth={2} /> {point}
              </p>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-miles-sky/50">{t("bz.auth.panel_footer")}</p>
      </div>

      <div className="flex flex-col px-5 py-8 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between">
          <Link to="/business" className="group inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-ink" data-testid="auth-back">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" strokeWidth={1.5} />
            {t("bz.auth.back")}
          </Link>
          <Link to="/" className="inline-flex items-center lg:hidden" aria-label="MILES — retour à l'accueil">
            <MilesLogo variant="blue" className="h-5" testId="auth-logo-mobile" />
          </Link>
        </div>

        <div className="flex flex-1 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="mx-auto w-full max-w-md py-12">
            <Building2 className="h-8 w-8 text-miles" strokeWidth={1.5} />
            <h2 className="mt-5 text-3xl font-semibold tracking-[-0.04em] text-ink sm:text-4xl" data-testid="auth-title">
              {isLogin ? t("bz.auth.login_t") : t("bz.auth.signup_t")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate">
              {isLogin ? t("bz.auth.login_s") : t("bz.auth.signup_s")}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
              {!isLogin && (
                <>
                  <input value={form.org_name} onChange={set("org_name")} placeholder={t("bz.auth.org")} required minLength={2}
                    data-testid="auth-org-input" className={inputCls} aria-label={t("bz.auth.org")} />
                  <input value={form.name} onChange={set("name")} placeholder={t("bz.auth.name")} required minLength={2}
                    data-testid="auth-name-input" className={inputCls} aria-label={t("bz.auth.name")} />
                  <select value={form.business_type} onChange={set("business_type")} data-testid="auth-type-input"
                    className={inputCls} aria-label="Type d'organisation">
                    {[["company", "Entreprise"], ["hotel", "Hôtel"], ["hotel_group", "Groupe hôtelier"],
                      ["concierge", "Conciergerie"], ["travel_agency", "Agence de voyage"],
                      ["event_agency", "Agence événementielle"], ["clinic", "Clinique"],
                      ["family_office", "Family office"], ["other", "Autre"]].map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </>
              )}
              <input type="email" value={form.email} onChange={set("email")} placeholder={t("bz.auth.email")} required
                data-testid="auth-email-input" className={inputCls} aria-label={t("bz.auth.email")} />
              <div>
                <input type="password" value={form.password} onChange={set("password")} placeholder={t("bz.auth.password")} required
                  minLength={isLogin ? 1 : 8} data-testid="auth-password-input" className={inputCls} aria-label={t("bz.auth.password")} />
                {!isLogin && <p className="mt-1.5 text-xs text-slate">{t("bz.auth.password_hint")}</p>}
              </div>

              {error && (
                <p className="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600" data-testid="auth-error">
                  {error}
                </p>
              )}

              <button type="submit" disabled={busy} data-testid="auth-submit"
                className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-miles text-sm font-semibold text-white shadow-[0_12px_32px_rgba(36,87,255,0.3)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : (
                  <>{isLogin ? t("bz.auth.submit_login") : t("bz.auth.submit_signup")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></>
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-slate">
              <Link to={isLogin ? "/business/inscription" : "/business/connexion"} data-testid="auth-switch-mode"
                className="font-semibold text-miles transition-colors hover:text-miles-dark">
                {isLogin ? t("bz.auth.to_signup") : t("bz.auth.to_login")}
              </Link>
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
